import { useCallback, useEffect, useMemo } from "react"

import { Flex, Typography } from "antd"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"

import Table from "../../../../Components/Table"
import { RoutesPaths } from "../../../../constants/user-routes"
import Icon from "../../../../Icon"
import { selectHomeActionAlerts, selectHomeActionAlertsFetching } from "../../redux/home.slice"
import AlertsCards from "./AlertsCards"
import { getActionAlertsColumns } from "./columns"

import styles from "../../styles.module.less"

const { Text } = Typography

const AlertsRequiringAction = ({ getAlerts }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const theme = useSelector((state) => state.nav.theme)
  const isMobile = useSelector((state) => state.nav.isMobile)
  const isDemo = useSelector((state) => state.auth.userInfo?.is_demo)
  const alerts = useSelector(selectHomeActionAlerts)
  const loading = useSelector(selectHomeActionAlertsFetching)

  // The five newest alerts that are actually waiting on the merchant, which is this table's own
  // question — the page's window does not scope it. Attention is the only status they can act on,
  // and the only one the status cell draws a remaining-time counter for. Ordered by `created_at`
  // rather than `alert_timestamp`: the ticket asks for creation date, and `alert_timestamp` is
  // nullable, which on a descending sort would float timestamp-less alerts to the top.
  // `pinned_first: false` asks for that ordering literally. The Alerts screen floats pinned rows
  // above everything and keeps doing so; this block has five slots and no pin control, so a pinned
  // row here would push a genuinely newer alert out of the block instead of highlighting anything.
  useEffect(() => {
    getAlerts({
      alert_status: "Attention",
      ordering: "-created_at",
      page_size: 5,
      pinned_first: false
    })
  }, [getAlerts])

  const columns = useMemo(() => getActionAlertsColumns({ t, theme, isDemo }), [t, theme, isDemo])

  // The address the Alerts list uses, so both entry points land on one screen.
  const openAlert = useCallback(
    (alert) => navigate(`${RoutesPaths.alerts.all}?open_alert=${alert?.alert_id}`),
    [navigate]
  )

  return (
    <div className={styles.card} data-testid="home-alerts-requiring-action">
      <Flex align="center" justify="space-between" gap={16} className={styles.cardHeader}>
        <Flex align="center" gap={8}>
          <Icon type="alerts" className={styles.cardHeaderIcon} />
          <Text strong className={styles.cardTitle}>
            {t("Alerts requiring action")}
          </Text>
        </Flex>
        <button
          type="button"
          className={styles.viewAllLink}
          onClick={() => navigate(RoutesPaths.alerts.all)}
          data-testid="home-alerts-view-all"
        >
          {t("View All")}
          <Icon type="chevron_right_small" />
        </button>
      </Flex>
      {isMobile ? (
        <AlertsCards
          alerts={alerts}
          loading={loading}
          theme={theme}
          isDemo={isDemo}
          onOpen={openAlert}
        />
      ) : (
        <Table
          columns={columns}
          dataSource={alerts}
          loading={loading}
          rowKey="alert_id"
          withOutPagination
          scroll={{ x: 1130 }}
          onRow={(record) => ({
            onClick: () => openAlert(record),
            style: { cursor: "pointer" }
          })}
        />
      )}
    </div>
  )
}

export default AlertsRequiringAction
